import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { useAtualizarUsuario } from '@/services/Configuracoes';
import type { UsuarioGestorApi } from '@/services/Configuracoes';

interface Props {
  usuario: UsuarioGestorApi | null;
  onOpenChange: (open: boolean) => void;
}

const PERFIS = ['Admin', 'Contador', 'Auxiliar', 'Visualizador'];

export function EditarUsuarioDialog({ usuario, onOpenChange }: Props) {
  const [nome, setNome] = useState('');
  const [status, setStatus] = useState('A');
  const [perfil, setPerfil] = useState('Visualizador');
  const { mutate, isPending, isError, data, reset } = useAtualizarUsuario();

  useEffect(() => {
    if (!usuario) return;
    setNome(usuario.Nome);
    setStatus(usuario.Status === 'I' ? 'I' : 'A');
    setPerfil(usuario.Perfil);
    reset();
  }, [usuario, reset]);

  const failed = isError || data?.status.resultado === false;

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
  }

  function handleSubmit() {
    if (!usuario || !nome) return;
    mutate(
      { Email: usuario.Email, Nome: nome, Status: status, Perfil: perfil },
      { onSuccess: response => response.status.resultado && handleOpenChange(false) }
    );
  }

  return (
    <Dialog open={!!usuario} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input value={usuario?.Email ?? ''} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="editar-usuario-nome">Nome</Label>
            <Input
              id="editar-usuario-nome"
              value={nome}
              onChange={e => setNome(e.target.value)}
              disabled={isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="editar-usuario-perfil">Perfil</Label>
            <Select value={perfil} onValueChange={setPerfil} disabled={isPending}>
              <SelectTrigger id="editar-usuario-perfil" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERFIS.map(p => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="editar-usuario-status">Status</Label>
            <Select value={status} onValueChange={setStatus} disabled={isPending}>
              <SelectTrigger id="editar-usuario-status" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">Ativo</SelectItem>
                <SelectItem value="I">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {failed && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{data?.status.mensagem ?? 'Não foi possível atualizar o usuário.'}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!nome || isPending}>
            {isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
